import { useState } from 'react'
import { apiConfig } from '../config/api'
import type { BasicInfo } from '../types/basic-setup'

export interface PurchaseRequest {
  pack_id: string
  practitioner_info: {
    name: string
    specialty?: string
    bio?: string
    interview_answers?: string[]
    target_location?: {
      city?: string
      radius_km?: number
      lat?: number
      lng?: number
    }
    cta_url?: string
    ideal_client?: string
    outcomes?: string
    proof_points?: string
    offer?: string
    objections?: string
    voice_guidelines?: string
    compliance_notes?: string
    service_regions?: string[]
    languages?: string[]
    brand_colors?: string[]
    logo_url?: string
    hero_image_url?: string
    availability_summary?: string
    booking_url?: string
    pricing_model?: string
    price_range?: Record<string, unknown>
  }
  selected_platforms: string[]  // ["meta"], ["google"], or ["meta", "google"]
  separate_packs?: boolean  // If true, create separate campaigns for each platform (one pack each)
}

export interface PurchaseResponse {
  client_secret: string
  campaign_id: string
  transaction_id: string
}

export function useSpotlightCheckout() {
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createPurchase = async (
    packId: string,
    basicInfo: BasicInfo,
    generatedAssets: any,
    selectedPlatforms: string[] = ['meta'],
    separatePacks: boolean = false
  ): Promise<PurchaseResponse> => {
    try {
      setIsProcessing(true)
      setError(null)

      const token = localStorage.getItem('firebase_id_token')
      if (!token) {
        throw new Error('Authentication required')
      }

      // Build practitioner_info from onboarding data
      const practitionerInfo = {
        name: basicInfo.name || '',
        specialty: basicInfo.specialty,
        bio: basicInfo.tagline,
        cta_url: generatedAssets?.landing_page?.slug 
          ? `${window.location.origin}/spotlight/${generatedAssets.landing_page.slug}`
          : undefined,
        ideal_client: basicInfo.who_you_help,
        outcomes: basicInfo.offer_primary_outcome,
        booking_url: basicInfo.booking_url,
        logo_url: basicInfo.logo_url,
        hero_image_url: basicInfo.photo_url,
        target_location: basicInfo.location
          ? {
              city: basicInfo.location.city,
              radius_km: 10.0
            }
          : undefined
      }

      const request: PurchaseRequest = {
        pack_id: packId,
        practitioner_info: practitionerInfo,
        selected_platforms: selectedPlatforms,
        separate_packs: separatePacks
      }

      const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/purchase`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to create purchase' }))
        throw new Error(errorData.detail || `HTTP ${response.status}`)
      }

      const data: PurchaseResponse = await response.json()
      return data
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create purchase'
      setError(errorMessage)
      throw err
    } finally {
      setIsProcessing(false)
    }
  }

  return { createPurchase, isProcessing, error }
}



